import type { User } from "oidc-client-ts";
import { env } from "./env";
import { getOrCreateDeviceId } from "../hooks/useDeviceRegister";
import type {
  EventType,
  OpenSession,
  TimeEventInput,
  TimelineEvent,
} from "../api/types";

export function getWorkerUrl(user: User | null): string {
  if (env.VITE_DEFAULT_WORKER_URL) return env.VITE_DEFAULT_WORKER_URL;
  const profile = user?.profile as
    { preferred_username?: string; sub?: string } | undefined;
  const sub = profile?.preferred_username ?? profile?.sub;
  if (!sub) {
    throw new Error(
      "No worker_url available: user is unauthenticated or sub claim missing",
    );
  }
  return `urn:senacle:worker:${sub}`;
}

export function getDisplayName(user: User | null): string {
  const profile = user?.profile as
    | {
        name?: string;
        preferred_username?: string;
        given_name?: string;
        family_name?: string;
        email?: string;
      }
    | undefined;
  if (!profile) return "Worker";
  if (profile.name) return profile.name;
  if (profile.given_name && profile.family_name)
    return `${profile.given_name} ${profile.family_name}`;
  return profile.preferred_username ?? profile.email ?? "Worker";
}

export function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}

/** Couleur d'avatar déterministe. Le backend ne stocke aucune couleur par
 * utilisateur (le design en prête une à chaque membre) : on la dérive du seed
 * pour qu'un même worker garde la même teinte d'un écran à l'autre. */
export function avatarColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360} 65% 45%)`;
}

/** Convention context_refs sur l'event activity_start : véhiculée verbatim
 * jusqu'à time_events.context_refs :
 * { note?, project_id?, work_item_id?, project_label?,
 *   task_refs?: [{task_id, label}], lte_refs?: [{task_id, label}] }.
 * project_id / work_item_id à plat, UN projet par activité : c'est la clé que
 * TAWA lit pour publier workforce.worklog.submitted (ADR « Imputation TAWA →
 * PM », 24/09/2026). Labels pré-résolus à l'écriture (pas de lookup à
 * l'affichage). Lecture tolérante : les events antérieurs à l'ADR portent
 * project_refs (liste, multi-projet), ceux d'avant le multi-select les clés
 * singulières project_ref/task_ref/lte_ref : readContextRefs retombe dessus
 * pour l'affichage ; TAWA ne les impute pas. */
export interface ActivityProjectRef {
  project_id: string;
  work_item_id?: string;
  label: string;
}

export interface ActivityTaskRef {
  task_id: string;
  label: string;
}

/** Terrain (LTE) : la tâche d'exécution n'a pas de titre backend, le label est
 * pré-résolu à l'écriture (execution_type + zone/site). */
export interface ActivityLteRef {
  task_id: string;
  label: string;
}

export interface ActivityContextRefs {
  note?: string;
  project_id?: string;
  work_item_id?: string;
  project_label?: string;
  /** Format antérieur à l'ADR imputation : lu, plus jamais écrit. */
  project_refs?: ActivityProjectRef[];
  task_refs?: ActivityTaskRef[];
  lte_refs?: ActivityLteRef[];
}

/** Vue plate des liens pour l'affichage ET l'édition : labels (chips) + IDs
 * (repré-remplissage des checkboxes, navigation). Tableaux vides = pas de lien. */
export interface ReadContextRefs {
  note?: string;
  projects: { id: string; label?: string; workItemId?: string }[];
  tasks: { id: string; label?: string }[];
  lteTasks: { id: string; label?: string }[];
}

export function readContextRefs(
  raw: Record<string, unknown> | null | undefined,
): ReadContextRefs {
  const refs = (raw ?? {}) as {
    note?: unknown;
    project_id?: unknown;
    work_item_id?: unknown;
    project_label?: unknown;
    project_refs?: unknown;
    task_refs?: unknown;
    lte_refs?: unknown;
    project_ref?: {
      label?: unknown;
      project_id?: unknown;
      work_item_id?: unknown;
    };
    task_ref?: { label?: unknown; task_id?: unknown };
    lte_ref?: { label?: unknown; task_id?: unknown };
  };
  const str = (v: unknown): string | undefined =>
    typeof v === "string" && v ? v : undefined;

  const projects = str(refs.project_id)
    ? [
        {
          id: refs.project_id as string,
          label: str(refs.project_label),
          workItemId: str(refs.work_item_id),
        },
      ]
    : Array.isArray(refs.project_refs)
      ? (refs.project_refs as ActivityProjectRef[])
          .filter((r) => str(r?.project_id))
          .map((r) => ({
            id: r.project_id,
            label: str(r.label),
            workItemId: str(r.work_item_id),
          }))
      : str(refs.project_ref?.project_id)
        ? [
            {
              id: refs.project_ref!.project_id as string,
              label: str(refs.project_ref?.label),
              workItemId: str(refs.project_ref?.work_item_id),
            },
          ]
        : [];

  const tasks = Array.isArray(refs.task_refs)
    ? (refs.task_refs as ActivityTaskRef[])
        .filter((r) => str(r?.task_id))
        .map((r) => ({ id: r.task_id, label: str(r.label) }))
    : str(refs.task_ref?.task_id)
      ? [
          {
            id: refs.task_ref!.task_id as string,
            label: str(refs.task_ref?.label),
          },
        ]
      : [];

  const lteTasks = Array.isArray(refs.lte_refs)
    ? (refs.lte_refs as ActivityLteRef[])
        .filter((r) => str(r?.task_id))
        .map((r) => ({ id: r.task_id, label: str(r.label) }))
    : str(refs.lte_ref?.task_id)
      ? [
          {
            id: refs.lte_ref!.task_id as string,
            label: str(refs.lte_ref?.label),
          },
        ]
      : [];

  return { note: str(refs.note), projects, tasks, lteTasks };
}

export interface SessionState {
  attendanceOpen: boolean;
  attendanceStart: number | null;
  activeActivity: ({ code: string; start: number } & ReadContextRefs) | null;
  onBreak: boolean;
  breakStart: number | null;
}

export function deriveSessionState(events: TimelineEvent[]): SessionState {
  const sorted = [...events].sort(
    (a, b) =>
      new Date(a.occurred_at).getTime() - new Date(b.occurred_at).getTime(),
  );

  const s: SessionState = {
    attendanceOpen: false,
    attendanceStart: null,
    activeActivity: null,
    onBreak: false,
    breakStart: null,
  };

  for (const ev of sorted) {
    const ts = new Date(ev.occurred_at).getTime();
    switch (ev.event_type) {
      case "attendance_in":
        s.attendanceOpen = true;
        s.attendanceStart = ts;
        s.activeActivity = null;
        s.onBreak = false;
        s.breakStart = null;
        break;
      case "attendance_out":
        s.attendanceOpen = false;
        s.attendanceStart = null;
        s.activeActivity = null;
        s.onBreak = false;
        s.breakStart = null;
        break;
      case "activity_start": {
        // Le chemin self-service (créneau timeline) écrit activity_code dans
        // context_refs, pas en colonne top-level : même fallback que buildBlocks,
        // sinon l'activité en cours n'est jamais dérivée (badge absent + borne
        // anti-chevauchement slotMaxEndMin désactivée).
        const refCode = (ev.context_refs as { activity_code?: unknown } | null)
          ?.activity_code;
        const code =
          ev.activity_code ??
          (typeof refCode === "string" ? refCode : undefined);
        if (code)
          s.activeActivity = {
            code,
            start: ts,
            ...readContextRefs(ev.context_refs),
          };
        break;
      }
      case "activity_end":
        s.activeActivity = null;
        break;
      case "break_start":
        s.onBreak = true;
        s.breakStart = ts;
        break;
      case "break_end":
        s.onBreak = false;
        s.breakStart = null;
        break;
    }
  }
  return s;
}

// Backend open-session state is an ADDITIVE overlay on the timeline-derived state, never
// a force-close. An open-session confirms attendance even when the badge-in event falls
// outside the fetched timeline window (overnight session, pushed out by a row limit). But
// its absence does NOT mean the worker is out: the open-sessions projection is cache-aside
// and can be transiently empty/stale (≤30s) right after a badge in/out. Falling back to the
// timeline-derived state in that case keeps attendance open as soon as the badge-in event
// is visible : read-your-writes via the timeline query invalidation.
export function reconcileSessionState(
  derived: SessionState,
  openSession: OpenSession | undefined,
): SessionState {
  if (openSession) {
    const start = new Date(openSession.opened_at).getTime();
    return {
      ...derived,
      attendanceOpen: true,
      attendanceStart: derived.attendanceStart ?? start,
    };
  }
  return derived;
}

// Détection plateforme par user-agent : faute d'app mobile native, on distingue
// un terminal mobile (téléphone/tablette) d'un poste desktop via le UA. Retourne
// un membre valide de l'enum source_type backend (record.go:48 : 'mobile' | 'web').
export function detectSourceType(): "mobile" | "web" {
  const ua =
    typeof navigator !== "undefined" ? (navigator.userAgent ?? "") : "";
  return /Android|iPhone|iPad|iPod|Mobile|Windows Phone|webOS|BlackBerry/i.test(
    ua,
  )
    ? "mobile"
    : "web";
}

export function buildCorrelationId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `cid-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export async function buildEvent(
  workerUrl: string,
  eventType: EventType,
  activityCode?: string,
): Promise<TimeEventInput> {
  const occurredAt = new Date().toISOString();
  const correlationId = buildCorrelationId();
  const sigInput = `${workerUrl}|${eventType}|${occurredAt}`;
  if (typeof crypto === "undefined" || !crypto.subtle) {
    throw new Error(
      "Web Crypto API indisponible : signature_hash impossible à calculer (contexte HTTPS requis).",
    );
  }
  const enc = new TextEncoder().encode(sigInput);
  const buf = await crypto.subtle.digest("SHA-256", enc);
  const signatureHash = Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return {
    worker_url: workerUrl,
    event_type: eventType,
    occurred_at: occurredAt,
    source_type: detectSourceType(),
    source_id: getOrCreateDeviceId(),
    confidence_level: "declared",
    signature_hash: signatureHash,
    correlation_id: correlationId,
    ...(activityCode ? { activity_code: activityCode } : {}),
  };
}

export function useOnlineStatus(): boolean {
  return typeof navigator !== "undefined" ? navigator.onLine : true;
}
